<template>
  <div
    class="max-w-1600 mx-auto w-full relative"
    :class="isDarkMode ? 'text-app-color': 'text-dark-600'"
  >
    <div
      class="px-20 md:px-70 pt-50 pb-150 mx-auto min-h-full flex-grow flex flex-col h-full bg-primary"
    >
      <!-- header -->
      <div class="md:flex items-start justify-between mb-40 relative">
        <div>
          <div class="flex items-center justify-between">
            <h1 class="text-24 font-bold leading-none">Discussions</h1>
          </div>
          <div class="mt-4" :class="isDarkMode ? 'text-app-color opacity-90' : 'text-dark-600'">
            <p class="text-16">
              There
              <span v-if="filteredDiscussions.length === 1">is</span>
              <span v-else>are</span>
              <span class="ml-5 font-bold">{{ countDiscussions }}</span>
              {{ activeState ? 'active' : 'resolved' }}
              discussion<span v-if="filteredDiscussions.length !== 1">s</span>
            </p>
          </div>
        </div>
        <div class="flex items-center justify-between mt-15 md:mt-0 self-start order-0 md:order-2">
          <BooksSelect
            v-if="selectedBooks[0]"
            :selected-books="selectedBooks"
            class="md:ml-10 border-2 rounded-full w-165"
            :class="isDarkMode ? 'border-hover' : 'border-grey-800 hover:bg-grey-150'"
            :default-book-id="parseInt($route.query.spaces)"
            :options="options"
            @onSelect="onSelect"
          />
          <ButtonModule
            prefix="icon-discussion"
            text="New discussion"
            class="bg-brand hover:bg-brand-hover text-white rounded-full ml-15 text-15 leading-none py-10 px-15"
            @click="openModal"
          />
        </div>
      </div>
      <!-- content -->
      <div>
        <div class="flex">
          <Switcher v-model="activeState" :is-dark-mode="isDarkMode" :titles="['Active', 'Resolved']" class="max-w-250" />
        </div>

        <div class="mt-20">
          <DiscussionItem
            v-for="(discussion, index) in draftDiscussions"
            :key="discussion.id"
            :has-border="index !== 0"
            :discussion="discussion"
          />
          <DiscussionItem
            v-for="(discussion, index) in filteredDiscussions"
            :key="discussion.id"
            :discussion="discussion"
            :has-border="index !== 0 || draftDiscussions.length !== 0"
            @click="openDiscussion(discussion)"
          />
        </div>

        <NewDiscussion
          v-if="modalIsOpen"
          :selected-book="selectedBooks[0]"
          @close="closeModal"
        />
      </div>
    </div>
  </div>
</template>

<script>
import ButtonModule from '@/components/shared/form-controls/ButtonModule'
import Switcher from '@/components/shared/Switcher'
import BooksSelect from '@/components/task-board/BooksSelect'
import DiscussionItem from '@/components/discussions/DiscussionItem'
import NewDiscussion from '@/components/discussions/NewDiscussion'

import { defineComponent, computed, ref, onMounted, nextTick, watch } from 'vue'
import { useStore } from 'vuex'
import { useRouter, useRoute } from 'vue-router'

export default defineComponent({
  components: {
    ButtonModule,
    Switcher,
    BooksSelect,
    DiscussionItem,
    NewDiscussion
  },

  setup () {
    const { state, getters, dispatch } = useStore()
    const router = useRouter()
    const route = useRoute()

    const modalIsOpen = ref()
    const activeState = ref(true)
    const selectedBooks = ref([])
    const allBooks = ref()

    const settings = computed(() => state.settings.settings)
    const headquarters = computed(() => state.team.headquarters)
    const showSidebar = computed(() => state.settings.showSidebar)
    const books = computed(() => state.book.books.filter(b => !b.isSection)) // TODO: books must be stored without Sections by default

    const draftDiscussions = computed(() => activeState.value ? state.discussions.draftDiscussions : [])

    const isDarkMode = computed(() => getters.isDarkMode)
    const isTeamAccount = computed(() => getters.isTeamAccount)

    const filteredDiscussions = computed(() => {
      if (activeState.value) {
        return state.discussions.discussions.filter(item => !item.resolvedAt)
      }
      return state.discussions.discussions.filter(item => item.resolvedAt).sort((a, b) => {
        if (a.resolvedAt > b.resolvedAt) return -1
        if (a.resolvedAt < b.resolvedAt) return 1
        return 0
      })
    })

    const countDiscussions = computed(() => {
      const disc = filteredDiscussions.value

      return `${disc.length > 0 ? disc.length : 'no'}`
    })

    watch(() => route.params.id, (to) => {
      if (!to) return

      router.replace({ query: { spaces: to } })
      const book = allBooks.value.find(item => item.id === +to)
      selectedBooks.value = [book]

      dispatch('getDiscussions', { bookIds: selectedBooks.value.map(b => b.id) })
      dispatch('getDraftDiscussions', { bookIds: selectedBooks.value.map(b => b.id) })
    })

    watch(() => route.query.resolved, val => {
      activeState.value = false
    })

    const options = computed(() => {
      return books.value.map(book => {
        return {
          id: book.id,
          title: book.title
        }
      })
    })

    onMounted(() => {
      nextTick(() => {
        if (route.query.resolved) {
          activeState.value = false
        }

        allBooks.value = books.value
        const bIds = route.query.spaces?.split(',').map(i => parseInt(i))

        allBooks.value.push(headquarters.value)

        selectedBooks.value = allBooks.value.filter(b => bIds.includes(b.id))

        dispatch('getDiscussions', { bookIds: selectedBooks.value.map(b => b.id) })
        dispatch('getDraftDiscussions', { bookIds: selectedBooks.value.map(b => b.id) })

        dispatch('getBook', route.params.id).then(res => {
          if (isTeamAccount.value && res.id && res.chat?.id) {
            dispatch('getMessages', {
              bookId: res.id,
              chatId: res.chat.id
            })
          }
        })
      })
    })

    async function onSelect (value) {
      selectedBooks.value = value

      dispatch('getDiscussions', { bookIds: selectedBooks.value.map(b => b.id) })
      dispatch('getDraftDiscussions', { bookIds: selectedBooks.value.map(b => b.id) })

      router.replace({ query: { spaces: selectedBooks.value.map(b => b.id).join(',') } })
    }

    function openModal () {
      modalIsOpen.value = true
    }

    function closeModal () {
      modalIsOpen.value = false
    }

    function openDiscussion (item) {
      router.push({ name: 'Discussion', params: { id: item.bookId, discussionId: item.id } })
    }

    return {
      // data
      options,
      modalIsOpen,
      activeState,
      selectedBooks,
      // computed
      settings,
      isDarkMode,
      showSidebar,
      draftDiscussions,
      countDiscussions,
      filteredDiscussions,
      // functions
      onSelect,
      openModal,
      closeModal,
      openDiscussion
    }
  }
})
</script>
